/* eslint-disable react/no-danger */
import { bannerManager } from '@envato/foundation-types'
import * as classnames from 'classnames'
import random from 'lodash-es/random'
import * as React from 'react'
import isEqual from 'lodash-es/isEqual'

import {
  reportHeaderBannerClick,
  reportHeaderBannerClose,
  reportHeaderBannerImpression
} from '../../../common/analytics/bannerAnalytics'
import {
  Consent,
  userHasOptedOutOfCookiesForCategory
} from '../../../common/utils/consent'

import { getCookie, setCookie } from '../../../common/utils/cookieHelper'

import {
  getLocalStorageItem,
  setLocalStorageItem
} from '../../../common/utils/localStorageHelpers'

import { parseLocation } from '../../../common/utils/urlHelper'

interface IProps {
  banners: bannerManager.headerBanners.IResult
  impressions: boolean
}

interface IState {
  bannerConfig: bannerManager.headerBanners.IBannerData | null
  isDismissed: boolean
  hasBindedDismissEvent: boolean
  hasBindedClickEvent: boolean
}

class HeadBanner extends React.Component<IProps, IState> {
  private $dom: HTMLDivElement | null

  constructor(props: IProps) {
    super(props)

    this.$dom = null

    // we should not rendering any thing from server side for two reasons:
    // 1. content will be cached through CDN, and we don't want it to be cached
    // 2. we will get react invalid checksum warning because server and client
    // render differnt content
    this.state = {
      bannerConfig: null,
      hasBindedClickEvent: false,
      hasBindedDismissEvent: false,
      isDismissed: false
    }

    this.onClick = this.onClick.bind(this)
    this.onDismiss = this.onDismiss.bind(this)
  }

  public componentDidMount() {
    this.pickBannerConfig()
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { bannerConfig } = this.state
    const { banners } = this.props
    const prevBanners = prevProps.banners
    const prevBannerConfig = prevState.bannerConfig

    if (!isEqual(prevBanners, banners)) {
      this.pickBannerConfig()
    }

    if (!isEqual(prevBannerConfig, bannerConfig)) {
      this.bindEvents()
    }
  }

  public render() {
    const { bannerConfig, isDismissed } = this.state

    if (!bannerConfig) {
      return null
    }

    const bannerContainerClassName = classnames('banner-root', {
      'banner-slide-up': isDismissed
    })

    return (
      <div
        className={bannerContainerClassName}
        ref={$dom => {
          this.$dom = $dom
        }}
      >
        <style>{bannerConfig.css}</style>
        <div
          className="banner-container"
          dangerouslySetInnerHTML={{ __html: bannerConfig.html }}
        />
      </div>
    )
  }

  private querySelector(selector: string) {
    if (this.$dom) {
      return this.$dom.querySelector(selector)
    }
    return null
  }

  private getCachedBannerIds() {
    const ids = getCookie({
      itemKey: 'marketplace_banners',
      consentType: Consent.marketing,
      defaultValue: JSON.stringify({})
    })

    return ids ? JSON.parse(ids) : {}
  }

  private getPageKey() {
    const path =
      (parseLocation({ pathname: window.location.pathname || '/' }) as Record<
        string,
        string
      >).category || 'global'
    return path.split('/')[0]
  }

  private pickBannerConfig() {
    const { banners } = this.props
    let bannerConfig = null

    if (banners && banners.length > 0) {
      const hasCategoryBanner = !!banners[0].categories
      // when there's no category banner, simply use 'global' as the pageKey
      const pageKey = hasCategoryBanner ? this.getPageKey() : 'global'
      const cachedBannerId = this.getCachedBannerIds()[pageKey]
      bannerConfig =
        banners.find(banner => banner.id == cachedBannerId) ||
        banners[random(banners.length - 1)]

      if (!cachedBannerId) {
        setCookie({
          itemKey: 'marketplace_banners',
          consentType: Consent.marketing,
          itemValue: JSON.stringify({
            ...this.getCachedBannerIds(),
            [pageKey]: bannerConfig.id
          })
        })
      }
    }

    this.setState(
      {
        bannerConfig,
        hasBindedClickEvent: false,
        hasBindedDismissEvent: false,
        isDismissed: this.hasBeenDismissed()
      },
      () => this.logImpression()
    )
  }

  private getBannerCacheId() {
    const { banners } = this.props
    const bannerId = banners && banners[0] ? banners[0].id : null
    if (bannerId) {
      return `banner_${bannerId}_cache`
    }
    return null
  }

  private onClick() {
    const { bannerConfig } = this.state
    if (bannerConfig) {
      const payload = {
        id: bannerConfig.id,
        name: bannerConfig.id
      }
      reportHeaderBannerClick(payload)
    }
  }

  private onDismiss(event: Event) {
    event.preventDefault()
    const bannerCacheId = this.getBannerCacheId()
    if (bannerCacheId) {
      setLocalStorageItem(bannerCacheId, 'hidden', Consent.marketing)
    }
    const { bannerConfig } = this.state
    if (bannerConfig) {
      const payload = {
        id: bannerConfig.id,
        name: bannerConfig.id
      }
      reportHeaderBannerClose(payload)
    }
    this.setState({
      isDismissed: true
    })
  }

  private hasBeenDismissed() {
    const bannerCacheId = this.getBannerCacheId()

    if (bannerCacheId) {
      return !!getLocalStorageItem(bannerCacheId, Consent.marketing)
    }

    return false
  }

  private bindDismissEvent() {
    if (this.$dom) {
      const dismissButtons = this.$dom.querySelectorAll('.js-banner__dismiss')

      if (dismissButtons) {
        this.setState({
          hasBindedDismissEvent: true
        })
        Array.from(dismissButtons).forEach(dismissButton => {
          dismissButton.addEventListener('click', this.onDismiss)
        })
      }
    }
  }

  private bindBannerClickEvent() {
    if (this.$dom) {
      const bannerLinkButtons = this.$dom.querySelectorAll('.js-banner__link')

      if (bannerLinkButtons) {
        this.setState({
          hasBindedClickEvent: true
        })
        Array.from(bannerLinkButtons).forEach(bannerLinkButton => {
          bannerLinkButton.addEventListener('click', this.onClick)
        })
      }
    }
  }

  private bindEvents() {
    const {
      bannerConfig,
      hasBindedDismissEvent,
      hasBindedClickEvent
    } = this.state
    const dismissButton = this.querySelector('.js-banner__dismiss')
    const bannerLinkButton = this.querySelector('.js-banner__link')
    // only bind the dismiss event when the banner is loaded
    if (bannerConfig) {
      if (dismissButton && !hasBindedDismissEvent) {
        this.bindDismissEvent()
      }
      if (bannerLinkButton && !hasBindedClickEvent) {
        this.bindBannerClickEvent()
      }
    }
  }

  private logImpression() {
    const { impressions } = this.props
    const { bannerConfig, isDismissed } = this.state
    const bannerCacheId = this.getBannerCacheId()

    if (!impressions) {
      return
    }

    if (
      bannerCacheId &&
      bannerConfig &&
      !isDismissed &&
      !userHasOptedOutOfCookiesForCategory(Consent.marketing)
    ) {
      const payload = {
        id: bannerConfig.id,
        name: bannerConfig.id
      }

      reportHeaderBannerImpression(payload)
    }
  }
}

export default HeadBanner
